import { useState } from 'react';
import { StoryblokRichtext } from 'storyblok-rich-text-react-renderer';
import { toast } from 'react-toastify';

import styles from './CheckoutCart.module.scss';
import { EmptyCart } from './components/EmptyCart/EmptyCart';

import { CartItem } from 'types/cart.types';

import { Button, ButtonTheme, ButtonVariant } from '@base-components/Button';

import { Column } from '@components/Column/Column';
import Loader from '@components/Loader/Loader';
import { NotificationBar } from '@components/NotificationBar/NotificationBar';
import { PaymentOptions } from '@components/PaymentOptions/PaymentOptions';

import { scrollToTop } from '@helpers/scroll-to-top';

import { useCouponMessages } from '@hooks/cart';
import { useCartWithOffer } from '@hooks/cart/useCartWithOffer';
import { useIsCouponCodeRedeemed } from '@hooks/cart/useIsCouponCodeRedemeed';
import { useIsCouponExpiredMessage } from '@hooks/cart/useIsCouponExpiredMessage';
import { useOffers } from '@hooks/cart/useOffers';
import { useRemoveItemsWithOffer } from '@hooks/cart/useRemoveItemsWithOffer';
import { useNotCancelledSubscription } from '@hooks/useNotCancelledSubscriptions';
import { useLocale } from '@hooks/useLocale';
import { useLogger } from '@hooks/useLogger';
import { useRedirect } from '@hooks/useRedirect';

import PromoCode from '@modules/PromoCode/PromoCode';

import CartItemOffer from '@theme/ShoppingCart/components/CartItem/CartItemOffer';
import { CartSummaryOffer } from '@theme/ShoppingCart/components/CartSummary/CartSummaryOffer';
import { RemoveOutOfStockItemsNotification } from '@theme/ShoppingCart/components/RemoveOutOfStockItemsNotification/RemoveOutOfStockItemsNotification';
import { aggregateOfferItems } from '@theme/ShoppingCart/helpers/aggregateCartItems';

import { getCouponDiscountMessages } from '@utils/getCouponDiscountMessage';
import getRuntimeConfig from '@utils/getRuntimeConfig';
import { Paths } from '@utils/paths/paths';
import { OfferItemType } from 'types/offer.types';
import { PlanContractType } from 'types/plan.types';

interface CheckoutCartProps {
  emptyCartText: StoryblokRichtext;
  twoOrMoreSameAppliancesText: string;
  youPayMonthlyText: string;
  totalText: string;
  monthlyText: string;
  discountCodeText: string;
  checkText: string;
  headerText: string;
  subHeaderText: string;
  subHeaderEmptyCartText: string;
  copiedUrlText: string;
  continueShoppingText: string;
  continueToCheckoutText: string;
  paymentOptions: string[];
  addMoreThanOneNotificationText: string;
  autoApplicableCouponInfoText: string;
  explainDeliveryFeeText: StoryblokRichtext;
  infoBoxes: any[];
}

export const CheckoutCart = ({
  emptyCartText,
  twoOrMoreSameAppliancesText,
  youPayMonthlyText,
  totalText,
  monthlyText,
  discountCodeText,
  checkText,
  headerText,
  subHeaderText,
  subHeaderEmptyCartText,
  copiedUrlText,
  continueShoppingText,
  continueToCheckoutText,
  paymentOptions,
  addMoreThanOneNotificationText,
  autoApplicableCouponInfoText,
  explainDeliveryFeeText,
  infoBoxes,
}: CheckoutCartProps) => {
  const [shouldShowOutOfStockError, setShouldShowOutOfStockError] = useState(false);
  const { country, language } = useLocale();
  const { data: cart, isLoading, refetch, isIdle } = useCartWithOffer();
  const removeItemsMutation = useRemoveItemsWithOffer();
  const { couponErrorMessages, couponDiscountMessages } = useCouponMessages();
  const isCouponExpiredMessage = useIsCouponExpiredMessage();
  const isCouponCodeRedeemedMessage = useIsCouponCodeRedeemed();
  const redirect = useRedirect();
  const { logger } = useLogger({ context: 'ShoppingCart' });
  const { data: subscriptions, isLoading: isSubscriptionsLoading } = useNotCancelledSubscription();

  const isFinanceContractType = cart?.items.some(
    item => item.offerItem?.type === OfferItemType.Main && item.offerItem?.contractType === PlanContractType.Finance
  );

  const { data: offersInTheCart, isLoading: areOffersLoading } = useOffers(
    cart?.items.map(i => i.offerContentSlug) || []
  );

  if (isLoading || cart === undefined || areOffersLoading || isSubscriptionsLoading) {
    return (
      <div className={styles.loaderContainer}>
        <Loader className={styles.loader} />
      </div>
    );
  }

  if (!isLoading && cart?.items.length === 0) {
    return (
      <EmptyCart
        emptyCartText={emptyCartText}
        headerText={headerText}
        subHeaderEmptyCartText={subHeaderEmptyCartText}
        continueShoppingText={continueShoppingText}
      />
    );
  }

  const copyUrlToClipboard = async sharedUrl => {
    await navigator.clipboard.writeText(sharedUrl);
  };

  const shareHandler = async () => {
    if (cart) {
      const sharedUrl = `${getRuntimeConfig('APP_URL')}/${country}-${language}/share-cart-offer?cartId=${cart.id}`;
      copyUrlToClipboard(sharedUrl);
      toast(copiedUrlText, {
        position: toast.POSITION.TOP_CENTER,
        type: toast.TYPE.SUCCESS,
      });
    }
  };

  const removeItems = async (items: CartItem[]) => {
    await refetch();

    return removeItemsMutation.mutate(items);
  };

  const removeAllOutOfStockItems = () => {
    const items = cart.items.filter(item => !item.isOnStock);

    return removeItemsMutation.mutate(items);
  };

  const continueToCheckout = () => {
    if (cart.hasOutOfStockItems) {
      setShouldShowOutOfStockError(true);
      scrollToTop();
      logger.info({
        message: `User with id ${cart?.user?.id} tried to continue to checkout with out of stock items in cart`,
        params: { userId: cart.user?.id, cartId: cart.id },
      });

      return;
    }
    redirect({ pageId: isFinanceContractType ? Paths.CheckoutFinanceCustomerDetails : Paths.CheckoutCustomerDetails });
  };

  const aggregatedItemsForOffer = aggregateOfferItems(cart.items as Required<CartItem>[]);
  const { forDiscountedPeriodMessage } = getCouponDiscountMessages(cart.coupon, couponDiscountMessages);

  const notifications = [...infoBoxes];

  if (isCouponExpiredMessage || isCouponCodeRedeemedMessage) {
    notifications.push({
      key: 'coupon-expired',
      title: '',
      variant: 'error',
      notificationText: isCouponExpiredMessage || isCouponCodeRedeemedMessage,
    });
  }

  const hasAtLeastOneMDA = cart.items.filter(i => i.offerItem.groupType === 'MDA').length === 1;
  if (hasAtLeastOneMDA && !cart.coupon && addMoreThanOneNotificationText && country === 'nl') {
    notifications.push({
      key: 'add-more-than-one',
      title: '',
      variant: 'alice',
      notificationText: addMoreThanOneNotificationText,
    });
  }

  if (
    cart.items.filter(item => item.offerItem.type === 'main').length === 1 &&
    subscriptions.length === 0 &&
    !cart.coupon &&
    addMoreThanOneNotificationText &&
    country === 'de'
  ) {
    notifications.push({
      key: 'add-more-than-one',
      title: '',
      variant: 'alice',
      notificationText: addMoreThanOneNotificationText,
    });
  }

  if (!isCouponExpiredMessage && cart.coupon && !notifications.some(n => n.key === cart.coupon.code)) {
    notifications.push({
      key: cart.coupon.code,
      title: '',
      variant: 'alice',
      notificationText: autoApplicableCouponInfoText,
    });
  }

  if (cart.items.filter(item => item.offerItem.type === 'main').length > 1 && explainDeliveryFeeText) {
    notifications.push({
      key: 'explain-delivery-fee',
      title: '',
      variant: 'alice',
      notificationText: explainDeliveryFeeText,
    });
  }

  if (!isLoading && cart?.items.length > 0) {
    return (
      <div className={styles.cart}>
        <div className={styles.cartTopSection}>
          <div className={styles.cartHeader}>
            <h2>{headerText}</h2>
            {!(!cart || isLoading || isIdle || cart?.items?.length === 0) && (
              <Button
                className={styles.shareButton}
                as="button"
                variant={ButtonVariant.Icon}
                icon="share"
                onClick={shareHandler}
                theme={ButtonTheme.Tertiary}
                dataAttribute={{ 'data-test-id': 'share-cart' }}
              ></Button>
            )}
          </div>
          <div className={styles.cartSubHeader}>{subHeaderText}</div>
        </div>
        <Column
          items={[
            <div className={styles.cartSummary}>
              {shouldShowOutOfStockError && cart?.hasOutOfStockItems && (
                <RemoveOutOfStockItemsNotification ctaAction={removeAllOutOfStockItems} />
              )}
              <PromoCode
                checkText={checkText}
                text={discountCodeText}
                couponErrorMessages={couponErrorMessages}
                className=""
              />
              <CartSummaryOffer
                totalText={totalText}
                monthlyText={monthlyText}
                couponDiscountMessage={forDiscountedPeriodMessage}
              />
              <div className={styles.infoBoxContainer}>
                {notifications.map(({ notificationText, title, variant }) => (
                  <NotificationBar
                    key={notificationText}
                    title={title}
                    variant={variant}
                    content={notificationText}
                    contentVariables={cart.coupon}
                  />
                ))}
              </div>
              <div className={styles.buttonsContainer}>
                <Button
                  as="button"
                  onClick={() => redirect({ pageId: Paths.ProductsOverview })}
                  variant={ButtonVariant.Basic}
                  isFullWidth
                  theme={ButtonTheme.SoftBlue}
                  dataAttribute={{ 'data-test-id': 'continue-shopping' }}
                >
                  {continueShoppingText}
                </Button>
                <Button
                  as="button"
                  onClick={continueToCheckout}
                  variant={ButtonVariant.Basic}
                  isFullWidth
                  isElevated
                  dataAttribute={{ 'data-test-id': 'go-to-customer-data' }}
                >
                  {continueToCheckoutText}
                </Button>
              </div>
              <div className={styles.paymentOptions}>
                <PaymentOptions options={paymentOptions} />
              </div>
            </div>,
            <div className={styles.cartItems}>
              {aggregatedItemsForOffer.map(item => {
                const offerItems =
                  offersInTheCart?.find(offer => offer.contentSlug === item.mainItem.offerContentSlug)?.offerItems ||
                  [];
                const possibleOfferItems = offerItems.filter(offerItem =>
                  offerItem.pricings.some(offerItemPricing => offerItemPricing.months === item.mainItem.pricing.months)
                );

                return (
                  <CartItemOffer
                    key={item.mainItem?.setId}
                    aggregatedCartItem={item}
                    couponDiscountMessage={forDiscountedPeriodMessage}
                    twoOrMoreSameAppliancesText={twoOrMoreSameAppliancesText}
                    youPayMonthlyText={youPayMonthlyText}
                    isCouponExpiredMessage={isCouponExpiredMessage}
                    isCouponCodeRedeemedMessage={isCouponCodeRedeemedMessage}
                    possibleOfferItems={possibleOfferItems}
                    removeItems={removeItems}
                  />
                );
              })}
            </div>,
          ]}
          noOfColumns={2}
          mobileBehavior={'stacked'}
          stickyColumn
          outerMargin={false}
          invert
          stickyOnMobile={false}
        />
      </div>
    );
  }
};
